'use client';

import { Copy, Download, X } from 'lucide-react';
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Fragment, useRef, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import useSWR from 'swr';
import IAvatar from '../Avatar/Index';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { domToBlob, domToDataUrl } from 'modern-screenshot';

interface ShareQuoteProps {
  paragraphContent: string[];
  onClose: () => void;
  open?: boolean;
  query?: string;
}

export default function ShareQuote(props: ShareQuoteProps) {
  const { open, onClose, paragraphContent, query } = props;
  const ref = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { data: userInfo } = useSWR('currentUserInfo');
  const { showSuccess, showError } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [copy, setCopy] = useState(false);
  const [download, setDownload] = useState(false);

  const handleCopyImage = async () => {
    if (!ref.current || copy) return;
    setCopy(true);

    try {
      const blob = await domToBlob(ref.current);

      if (!blob) {
        showError(t('Failed to copy image'));
        return;
      }
      // 使用 Clipboard API 复制到剪贴板
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob,
        }),
      ]);
      showSuccess(t('Image copied to clipboard'));
    } catch (err) {
      console.error('Failed to generate image:', err);
      showError(t('Failed to generate image'));
    } finally {
      setCopy(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!ref.current || download) return;
    setDownload(true);
    try {
      const dataUrl = await domToDataUrl(ref.current);
      // 创建下载链接
      const link = document.createElement('a');
      link.download = `pythra-search-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();

      showSuccess(t('Image downloaded'));
    } catch (err) {
      console.error('Failed to download image:', err);
      showError(t('Failed to download image'));
    } finally {
      setDownload(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-200"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform rounded-2xl bg-[#E9E9E9] dark:bg-[#111111] border border-light-200 dark:border-dark-200 p-5 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between pb-5">
                  <DialogTitle className="text-lg font-medium leading-6 dark:text-white">
                    {t('Share quote')}
                  </DialogTitle>
                  <CloseButton>
                    <X />
                  </CloseButton>
                </div>
                <div className="max-h-[400px] bg-black rounded-xl overflow-scroll">
                  <div
                    ref={ref}
                    className="px-4 pb-6"
                    style={{
                      background: 'url(/image/bg_share.png) top center no-repeat',
                      backgroundSize: '100% 100%',
                    }}
                  >
                    <div className="flex items-center justify-center gap-2 py-6">
                      <Image src="/logo.svg" width={24} height={24} alt="Pythra Research" />
                      Pythra Research
                    </div>
                    <div className="border dark:border-white/10 rounded-lg p-5 text-xs bg-white/5">
                      <div className="flex items-end justify-end mb-5">
                        <div className="wrap-break-word bg-[#1e1e1e] rounded-2xl text-xs py-2.5 px-3">
                          {query}
                        </div>
                      </div>
                      <div className="space-y-5">
                        {paragraphContent.map((p, i) => (
                          <div
                            key={p}
                            className={cn('break-all text-xs leading-4', {
                              'line-clamp-5': i !== 1,
                            })}
                            style={
                              i !== 1
                                ? ({
                                    background: `linear-gradient(${i === 0 ? 'rgb(30 30 30) 20%' : 'rgb(81 81 81) 0%'}, ${i === 0 ? 'rgb(81 81 81) 100%' : 'rgb(30 30 30) 90%'}) text`,
                                    WebkitTextFillColor: 'transparent',
                                    textFillColor: 'transparent',
                                  } as any)
                                : {}
                            }
                          >
                            {p}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-[#7a7a7a] mt-5 text-xs">
                        <span>{t('Generated by Pythra Research')}</span>
                        <div className="flex items-center gap-1">
                          <IAvatar
                            src={userInfo?.avatar ?? ''}
                            name={userInfo?.name ?? ''}
                            size={16}
                          />
                          {userInfo?.name}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row items-end justify-end space-x-4 mt-6">
                  <button
                    onClick={handleCopyImage}
                    className="text-text-secondary h-8 text-sm px-4 rounded-lg bg-[#292929] hover:bg-[#292929]/70  transition duration-200 flex items-center gap-1"
                  >
                    <Copy size={16} />
                    {t('Copy')}
                    {copy ? '...' : ''}
                  </button>
                  <button
                    onClick={handleDownloadImage}
                    className="text-black text-sm h-8 rounded-lg px-4 bg-linear-to-r from-[#67FFFF] to-[#A772FF] transition duration200 flex items-center gap-1"
                  >
                    <Download size={16} />
                    {t('Download')}
                    {download ? '...' : ''}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
